import { ReportRow, InvoiceStatus } from "./types";
import { InvoiceRecord } from "src/config/invoice-tracker-client";

/**
 * Reconciles a report row's FACD invoice data against InvoiceTracker records.
 * Updates invoiceStatus, trackerTotal, and adjusts invoicesIssued/stEnding when
 * the Tracker is the authoritative source (manual configs).
 */
export function reconcileRow(
  row: ReportRow,
  trackerRecords: InvoiceRecord[],
): ReportRow {
  const boundary = row.firstInvoiceBreakdownPeriod;
  const currencyMatched = trackerRecords.filter(
    (r) =>
      r.invoiceCurrencyCode === row.currencyCode &&
      (!boundary || r.billingPeriod >= boundary),
  );
  const trackerTotal = currencyMatched.reduce((sum, r) => sum + r.amount, 0);
  const facdTotal = row.invoicesIssued;
  const status = determineStatus(
    facdTotal,
    trackerTotal,
    currencyMatched.length,
  );

  // Use Tracker amount whenever Tracker has records (source of truth for CSV)
  const invoicesIssued =
    trackerTotal > 0 || currencyMatched.length > 0 ? trackerTotal : facdTotal;

  return {
    ...row,
    invoiceStatus: status,
    trackerTotal,
    invoicesIssued,
    stEnding: row.stIssuance + row.cumulativeReclass - invoicesIssued,
  };
}

/**
 * Determines invoice reconciliation status based on FACD vs Tracker totals.
 */
function determineStatus(
  facdTotal: number,
  trackerTotal: number,
  trackerRecordCount: number,
): InvoiceStatus {
  if (trackerRecordCount === 0) {
    return facdTotal > 0 ? "missing" : "unreconciled";
  }
  if (facdTotal === 0 && trackerTotal > 0) {
    return "tracker-sourced";
  }
  // Allow 1 unit tolerance for rounding
  if (Math.abs(facdTotal - trackerTotal) <= 1) {
    return "confirmed";
  }
  return "amount-mismatch";
}
